import React from 'react';
import * as yup from 'yup';
import { FormikValues } from 'formik';
import { formatShortDate } from 'modules/Common/utilities';
import { FormikTextField } from 'modules/Common/components';
import { RecordDefinition } from 'modules/Pilot/BatchRecord/SingleView/components/RecordTable/RecordTable';
import { BatchRecord, PouchPreparation } from 'modules/Pilot/BatchRecord/domain';
import {
  RecordFoilPouchLotNumberActions,
  recordFoilPouchLotNumber,
  AmendFoilPouchLotNumberActions,
  amendFoilPouchLotNumber,
} from 'modules/Pilot/BatchRecord/actions';
import { PartStatus } from 'modules/RecordManagement';
import { UserPermission } from 'modules/Core/types';
import { DataTableRow } from '../SimpleDataTable/TableData';

interface Values extends FormikValues {
  lotNumber: string;
  comment?: string;
}
export default (
  batchRecord: BatchRecord,
  itemsBeingEdited: { key: string; itemId: string }[],
  userPermissions: UserPermission[]
): RecordDefinition<Values> => {
  const dialogName = 'pilot/batch-records/POUCH_PREPARATION';
  const recordId = batchRecord.id;
  const { status, subRecords, requiredPermissions } = batchRecord.deposition.foilPouches;

  const itemBeingEdited = itemsBeingEdited.find(
    item =>
      item.key === `${dialogName}_EDIT` &&
      subRecords &&
      subRecords.findIndex(sr => sr.id === item.itemId) > -1
  );

  const rowForEditing = itemBeingEdited
    ? subRecords.find(sr => sr.id === itemBeingEdited.itemId)
    : null;

  const isAllowedToEdit = () => {
    if (status === PartStatus.ReadOnly || status === PartStatus.Locked) return false;

    let result = true;
    requiredPermissions?.forEach(req => {
      if (userPermissions.find(perm => perm.name === req) === undefined) result = false;
    });

    return result;
  };

  const newSubmit = (values: Values, _, dispatch) =>
    dispatch(recordFoilPouchLotNumber(recordId, { ...values }));
  const editSubmit = (values: Values, _, dispatch) =>
    dispatch(amendFoilPouchLotNumber(recordId, { ...values, subRecordId: rowForEditing?.id }));

  const createDataTableRow = (row: PouchPreparation): DataTableRow => ({
    key: row.id,
    fields: {
      lotNumber: { value: row.lotNumber },
      preparedBy: { user: row.preparedBy.user.name, date: formatShortDate(row.preparedBy.date) },
    },
  });

  const validation = {
    lotNumber: yup
      .string()
      .defined()
      .min(1),
  };

  const editValidation = {
    ...validation,
    comment: yup
      .string()
      .defined()
      .required()
      .min(5),
  };

  return {
    title: 'Foil Pouch Preparation',
    tableData: {
      columns: [
        { accessor: 'lotNumber', label: 'Foil Pouch Lot Number' },
        { accessor: 'preparedBy', label: 'Prepared by', isSignOff: true },
      ],
      rows: subRecords ? subRecords.map(row => createDataTableRow(row)) : [],
    },
    canAddRow: status === PartStatus.Incomplete || status === PartStatus.Complete,
    canEditRows: isAllowedToEdit(),
    formDefinition: {
      title: 'Foil Pouch Preparation',
      dialogName,
      apiAction: rowForEditing
        ? AmendFoilPouchLotNumberActions.CALL
        : RecordFoilPouchLotNumberActions.CALL,
      onSubmit: rowForEditing ? editSubmit : newSubmit,
      initialValues: {
        lotNumber: rowForEditing ? rowForEditing.lotNumber : '',
      },
      form: () => (
        <>
          <FormikTextField name="lotNumber" label="Foil Pouch Lot Number" />
          {rowForEditing && <FormikTextField name="comment" label="Reason for change" multiline />}
        </>
      ),
      validationSchema: (): yup.ObjectSchema<Values> =>
        yup
          .object()
          .shape(rowForEditing ? editValidation : validation)
          .defined(),
    },
  };
};
